import { datadogLogs } from "@datadog/browser-logs";
import { useEffect, useMemo, useState } from "react";

import { gql } from "@examspark/api";
import { LtiLaunchToken } from "@examspark/ims-lti";
import {
  AppLicense,
  SsoLoginToken,
  Workspace,
  WorkspaceUser,
} from "@examspark/types";
import { useJwt } from "@examspark/use-jwt";

import { signInToken, SignInTokenParams } from "../auth";
import { useAppContextSerializable } from "../providers/AppContext";
import { isLtiLaunchToken } from "../providers/LTIProvider";

/**
 * Fetch workspace, workspace-user, and app-license data to ensure the user is
 * who they say they are, and the workspace is not bogus.
 */
const fetchAuthData = async (token: LtiLaunchToken | SsoLoginToken) => {
  let workspace: Workspace | undefined;
  let workspaceUser: WorkspaceUser | undefined;

  if (token.workspaceId == null) return;

  if (token.user != null) {
    workspaceUser = (await gql.operations.workspace.getWorkspaceUser(
      token.workspaceId,
      token.user.id,
    )) as WorkspaceUser;

    workspace = workspaceUser?.workspace ?? undefined;

    if (!workspaceUser) {
      console.warn(
        "Workspace user not found",
        token.workspaceId,
        token.user.id,
      );
    }
  } else if (isLtiLaunchToken(token) && token.workspace != null) {
    workspace = token.workspace;
  }

  if (workspace == null) {
    console.warn("Workspace not found", token.workspaceId);
    return;
  }

  let appLicense: AppLicense | undefined;

  if (token.appId != null) {
    appLicense = await gql.operations.app.getAppLicense(
      token.appId,
      token.workspaceId,
    );

    if (!appLicense) {
      console.warn("App License not found", token.appId, token.workspaceId);
      return;
    }
  }

  return {
    workspace,
    workspaceUser,
    appLicense,
  };
};

/**
 * Hook for token-based login. This is sister to the `useLogin` hook. They
 * should ideally have parallel naming someday (ex: `useTokenLogin` and
 * `useLogin`).
 *
 * Once logged in, this hook dispatches:
 *  - `SET_USER`
 *  - `SET_WORKSPACE`
 */
export const useTokenAuth = (token: string | null, purpose: "sso" | "lti") => {
  // tokens can disappear.
  const [cachedToken, setCachedToken] = useState<string | null>(token);
  useEffect(() => {
    if (token == null) return;
    setCachedToken(token);
  }, [token]);

  const { data: decoded, error } = useJwt<LtiLaunchToken | SsoLoginToken>(
    cachedToken,
  );
  const [, dispatch] = useAppContextSerializable();
  const [loginState, setLoginState] = useState<
    "failed" | "invalid_token" | "in_progress" | "completed" | undefined
  >(
    //
    // It's crucial that on the first render, this hook returns an appropriate
    // state based on whether a token exists. If it returns undefined, consuming
    // components may redirect the user to the login page and interfere with
    // the token signin.
    //
    cachedToken == null || decoded?.purpose !== purpose
      ? undefined
      : "in_progress",
  );

  useEffect(() => {
    if (error) setLoginState("invalid_token");
  }, [error]);

  useEffect(() => {
    if (decoded?.purpose !== purpose) {
      setLoginState(undefined);
      return;
    }

    setLoginState("in_progress");

    const processTokenAuth = async () => {
      await dispatch({ type: "CLEAR" });

      let cognitoSignInTokenParams: SignInTokenParams | undefined;

      if (isLtiLaunchToken(decoded)) {
        if (decoded.authUserTokens != null) {
          cognitoSignInTokenParams = {
            clientId: decoded.authAppClientId,
            idToken: decoded.authUserTokens?.IdToken,
            accessToken: decoded.authUserTokens?.AccessToken,
            refreshToken: decoded.authUserTokens?.RefreshToken,
          };
        }
      } else {
        cognitoSignInTokenParams = decoded.signInParams;
      }

      if (cognitoSignInTokenParams != null) {
        const user = signInToken(cognitoSignInTokenParams);

        if (user == null) {
          console.warn("Sign-in failed");
          setLoginState("failed");
        }
      } else {
        console.warn("Invalid token; missing sign-in data");
        setLoginState("failed");
      }

      await dispatch({
        type: "SET_USER_SSO",
        data: {
          user: decoded.user,
        },
      });

      const rsp = await fetchAuthData(decoded);

      if (rsp != null) {
        const { workspace, appLicense, workspaceUser } = rsp;
        await dispatch({
          type: "SET_WORKSPACE",
          data: {
            app: {
              id: appLicense?.appId ?? undefined,
            },
            workspace,
            appLicense,
            workspaceUser,
          },
        });
      }

      setLoginState("completed");
    };

    processTokenAuth().catch((e) => {
      console.error(e);
      setLoginState("failed");
    });

    datadogLogs.setGlobalContextProperty("appId", decoded.appId);
    datadogLogs.setGlobalContextProperty("appKey", decoded.appKeyId);
    datadogLogs.setGlobalContextProperty("workspaceId", decoded.workspaceId);
    datadogLogs.setGlobalContextProperty("token", decoded);
  }, [decoded, purpose, dispatch]);

  return useMemo(() => ({ loginState, decoded }), [decoded, loginState]);
};
